/**
 * C.5 — Obsidian Vault window.
 *
 * Three-column flex layout with adjustable grippers and minimum sizes
 * per Q2. Click-on-wikilink opens in pane B per Q28 (always split).
 *
 *   ┌─────────┬──────────────┬────────┐
 *   │  Tree   │   Editor A   │ Graph  │
 *   │         │     ║        │        │
 *   │         │  (split B)   │────────│
 *   │         │              │Backlnk │
 *   └─────────┴──────────────┴────────┘
 *
 * Layout state (widths + split-open) is owned by useResizablePanes.
 * Note model is read from `loadSampleVault()` (committed fixture) for
 * the C.5 cut. C.9 swaps in Graph-backed loaders for live mode.
 *
 * Font fix scoped via ObsidianVaultWindow.css so the WindowFrame's
 * default monospace doesn't leak through to form controls.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useConvex, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { testIds } from '../../lib/testIds';
import type { ParsedNote } from '../../lib/vault/parse';
import { useOptionalTenantBranding } from '../../lib/tenant/TenantBrandingContext';
import { useShowcaseSessionId } from '../../lib/showcase/showcaseSession';
import { useShowcaseVisible } from '../../lib/showcase/useShowcaseVisible';
import { useResolvedPortalSlug } from '../../lib/tenant/portalSlugFromUrl';
import { useLocation, useNavigate } from '@tanstack/react-router';

/**
 * Reads `?vault=<id>` from the active URL via the router's
 * `useLocation()` (race-free, refresh-friendly, shareable). We
 * deliberately don't use `useSearch({from: ...})` because the
 * vault window also mounts on `/` (not just `/c/$slug/desktop`)
 * and a typed `from:` would throw on the non-portal route.
 * Returns null when the param is missing or empty.
 */
function useVaultSearchParam(): string | null {
  const { searchStr } = useLocation();
  if (!searchStr || searchStr.length === 0) return null;
  try {
    const params = new URLSearchParams(
      searchStr.startsWith('?') ? searchStr.slice(1) : searchStr,
    );
    const v = params.get('vault');
    return v && v.length > 0 ? v : null;
  } catch {
    return null;
  }
}
import { loadSampleVault } from '../../lib/vault/sampleVault';
import {
  createInMemoryStore,
  createInMemoryTransport,
  createSaveProvider,
  type SaveProviderState,
  type VaultSaveProvider,
} from '../../lib/vault/saveProvider';
import { useAction, useMutation } from 'convex/react';
import {
  acquireGraphToken,
  connectMicrosoftAccountRedirect,
  getActiveAccount,
  getMsalAccountTenantId,
} from '../../lib/graph/msalClient';
import { mapPortableSiteForConvex } from '../nipe/VaultDropProvisionPanel';
import type { Id } from '../../../convex/_generated/dataModel';
import {
  createSharePointTransport,
  type SharePointNoteRef,
} from '../../lib/vault/sharepointSaveProvider';
import { useResizablePanes, DEFAULT_PANES_CONFIG } from './useResizablePanes';
import { VaultTreePane } from './VaultTreePane';
import { VaultEditorPane } from './VaultEditorPane';
import { VaultGraphPane } from './VaultGraphPane';
import { VaultBacklinksPane } from './VaultBacklinksPane';
import { SearchPalette } from './SearchPalette';
import { VaultExportMenu } from './VaultExportMenu';
import type { AssetRowInput } from '../../lib/vault/export/hydrateAssetBytes';
import { useIsOperator } from '../../lib/auth/useIsOperator';
import type { PortableSite } from '../../lib/portable-site';
import { FlowEditor } from './flow/FlowEditor';
import { setActiveFlow } from './flow/activeFlow';
import { useWindowContext } from '../desktop/WindowContext';
import { subscribeVaultSelect } from './activeVaultSelection';
import './ObsidianVaultWindow.css';

const WIN_FONT =
  "var(--xp-font-body)";

export function ObsidianVaultWindow() {
  // D.2 — operator-aware export. useIsOperator() returns false during
  // Clerk hydration so the SP export button doesn't flash for non-
  // operators. The real provisioner-kick callback is wired below,
  // after the demo/session state it depends on.
  const isOperator = useIsOperator();
  const provisionFromPortableSite = useMutation(
    api.vaultExportProvisioning.provisionFromPortableSite,
  );
  const kickVaultExportProvisioner = useAction(
    api.vaultExportProvisioning.kickVaultExportProvisioner,
  );

  // F6 — live Convex vault tables override the committed sample vault
  // when present for the portal. Sample remains the bootstrap fallback
  // for portals with no imported vault yet.
  const [isDemoForVault] = useShowcaseVisible();
  const demoSessionIdForVault = useShowcaseSessionId();

  // Real onExportToSharePointSite — mirrors VaultDropProvisionPanel
  // (D.3.5). Returns the jobId so the SP-site export target can
  // surface it in the info toast.
  const onExportToSharePointSite = useCallback(
    async (site: PortableSite): Promise<{ jobId?: string } | void> => {
      if (getActiveAccount() === null) {
        // No popup-bridge from a menu click — full-page redirect to
        // Microsoft sign-in. Resume-on-return for the menu path is
        // tracked as F-VAULT-MENU-MSAL-RESUME (use VaultDropProvisionPanel's
        // stash-and-resume model for the canonical UX).
        try {
          await connectMicrosoftAccountRedirect();
        } catch {
          /* navigates away on success; throws in test env */
        }
        throw new Error(
          'Connect your Microsoft account first, then try the export again.',
        );
      }
      const graphAccessToken = await acquireGraphToken();
      const operatorTenantId = getMsalAccountTenantId();
      if (operatorTenantId === null) {
        throw new Error(
          'Microsoft sign-in returned no tenant ID. Sign out and back in.',
        );
      }
      const title = `${site.sourceLabel ?? 'Vault'}`;
      const result = await provisionFromPortableSite({
        site: mapPortableSiteForConvex(site),
        title,
        sourceLabel: site.sourceLabel ?? 'vault',
        ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
      });
      if (!result.ok) {
        throw new Error(result.reason);
      }
      const jobId = result.jobId as Id<'portalProvisioningJobs'>;
      const kick = await kickVaultExportProvisioner({
        jobId,
        graphAccessToken,
        operatorTenantId,
      });
      if (!kick.ok) {
        throw new Error(kick.reason);
      }
      return { jobId: String(jobId) };
    },
    [
      provisionFromPortableSite,
      kickVaultExportProvisioner,
      isDemoForVault,
      demoSessionIdForVault,
    ],
  );
  // 2026-05-28 — useResolvedPortalSlug is the standard hook for
  // registered-window slug detection. Combines the React context
  // (when present) with /c/<slug>/... URL fallback, so this works
  // regardless of which WindowProvider rendered us. See
  // lib/tenant/portalSlugFromUrl.ts.
  const portalSlugForVault = useResolvedPortalSlug() ?? '';

  // 2026-05-28 — list ALL vaults for the portal so the user can pick
  // between multiple imports. Each "Confirm import" creates a new
  // vault row; without a picker, older imports become orphaned.
  // Default selection follows getBySlug's most-recent-first order
  // until the user explicitly switches.
  const portalVaults = useQuery(
    api.vaults.listVaults,
    portalSlugForVault
      ? {
          clientSlug: portalSlugForVault,
          ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
        }
      : 'skip',
  );
  const sortedVaults = useMemo(() => {
    if (!Array.isArray(portalVaults) || portalVaults.length === 0) return [];
    return [...portalVaults].sort((a, b) =>
      (b.createdAt ?? '') > (a.createdAt ?? '') ? 1 : -1,
    );
  }, [portalVaults]);
  // 2026-05-28 — selected vault lives in the URL as
  // `?vault=<id>` on the portal-desktop route. The URL is the
  // race-free, refresh-friendly, shareable source of truth. The
  // import wizard's "Open vault" CTA navigates with this param
  // set; the picker swaps it via router.navigate. Both writes go
  // through the same router primitive — no sessionStorage, no
  // CustomEvent.
  //
  // urlSelectedVaultId can be undefined when we're not on a portal
  // route (vault window also mounts on /). In that case
  // effectiveVaultId falls back to the most-recent.
  const urlSelectedVaultId = useVaultSearchParam();
  const navigate = useNavigate();
  const effectiveVaultId =
    urlSelectedVaultId ??
    (sortedVaults[0]?._id ? String(sortedVaults[0]._id) : null);

  // liveVault is the single row matching effectiveVaultId; falls
  // back to the most-recent (getBySlug) when no specific id is set.
  const liveVault = useMemo(() => {
    if (effectiveVaultId === null) return null;
    return sortedVaults.find((v) => String(v._id) === effectiveVaultId) ?? null;
  }, [sortedVaults, effectiveVaultId]);
  const liveNotes = useQuery(
    api.vaults.listNotesByVault,
    liveVault?._id
      ? {
          vaultId: String(liveVault._id),
          ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
        }
      : 'skip',
  );
  const liveFlows = useQuery(
    api.vaults.listFlowsByVault,
    liveVault?._id
      ? {
          vaultId: String(liveVault._id),
          ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
        }
      : 'skip',
  );
  // G.2 — vault assets for the export pipeline. Bytes are fetched
  // on demand via api.vaults.getAssetDownloadUrl (in VaultExportMenu).
  const liveAssets = useQuery(
    api.vaults.listAssetsByVault,
    liveVault?._id
      ? {
          vaultId: String(liveVault._id),
          ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
        }
      : 'skip',
  );
  const convexClient = useConvex();
  // G.2 — flatten asset rows into the shape hydrateAssetBytes consumes
  // + close over a per-call URL resolver. Memoized so the export menu
  // doesn't reconstruct callbacks on every keystroke.
  const assetRows = useMemo<AssetRowInput[]>(() => {
    if (!liveAssets) return [];
    return liveAssets.map((a) => ({
      id: String(a._id),
      path: a.path,
      mimeType: a.mimeType,
      sizeBytes: a.sizeBytes,
      lastModified: a.lastModified,
    }));
  }, [liveAssets]);
  const urlForAsset = useCallback(
    async (assetId: string): Promise<string | null> => {
      try {
        return await convexClient.query(api.vaults.getAssetDownloadUrl, {
          assetId,
          ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
        });
      } catch {
        return null;
      }
    },
    [convexClient, isDemoForVault, demoSessionIdForVault],
  );
  const sample = useMemo(() => loadSampleVault(), []);
  const notes: ParsedNote[] = liveNotes && liveNotes.length > 0
    ? liveNotes.map((r): ParsedNote => {
        let frontmatter: Record<string, unknown> = {};
        try {
          frontmatter = JSON.parse(r.frontmatterJson) as Record<string, unknown>;
        } catch {
          /* keep as empty */
        }
        return {
          path: r.path,
          name: r.name,
          body: r.body,
          raw: r.body,
          frontmatter,
          wikilinks: r.wikilinks ?? [],
          embeds: [],
          markdownLinks: r.markdownLinks ?? [],
          tags: r.tags ?? [],
          callouts: [],
        };
      })
    : sample.notes;
  const flows = liveFlows && liveFlows.length > 0
    ? liveFlows.map((f) => ({
        path: f.path,
        name: f.name,
        raw: JSON.stringify({ nodes: JSON.parse(f.nodesJson), edges: JSON.parse(f.edgesJson) }),
        version: 1,
        flowName: f.name,
        nodes: JSON.parse(f.nodesJson) as unknown[],
        edges: JSON.parse(f.edgesJson) as unknown[],
      }))
    : sample.flows;
  const { openWindow, focusWindow } = useWindowContext();

  const [paneAPath, setPaneAPath] = useState<string | null>(
    notes[0]?.path ?? null,
  );
  const [paneBPath, setPaneBPath] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  // F-NIPE-DESKTOP-AWARENESS — register the read-only bridge NIPE
  // reads via `readWindowContext('/obsidian-vault')`. The snapshot
  // mirrors the state hooks above; refs keep the getter cheap (no
  // re-registration per render).
  const vaultBridgeRef = useRef({
    paneAPath,
    paneBPath,
    paletteOpen,
    editing,
    noteCount: notes.length,
  });
  vaultBridgeRef.current = {
    paneAPath,
    paneBPath,
    paletteOpen,
    editing,
    noteCount: notes.length,
  };
  useEffect(() => {
    window.__repancyObsidianVault = () => ({
      focusedNotePath: vaultBridgeRef.current.paneAPath,
      paneBNotePath: vaultBridgeRef.current.paneBPath,
      splitOpen: vaultBridgeRef.current.paneBPath !== null,
      editing: vaultBridgeRef.current.editing,
      searchOpen: vaultBridgeRef.current.paletteOpen,
      noteCount: vaultBridgeRef.current.noteCount,
    });
    return () => {
      delete window.__repancyObsidianVault;
    };
  }, []);

  // G.1 — Convex etag-patch mutation used by the SP transport after a
  // successful PUT.
  const updateNoteAfterPut = useMutation(api.vaults.updateNoteAfterPut);

  // G.1 — path → externalRef map kept in a ref so the SP transport can
  // resolve the latest etag at PUT time without rebuilding the
  // provider. The map is recomputed on every liveNotes change.
  const refMapRef = useRef<Map<string, SharePointNoteRef>>(new Map());
  const liveVaultIdRef = useRef<string | null>(null);

  useEffect(() => {
    const map = new Map<string, SharePointNoteRef>();
    for (const r of liveNotes ?? []) {
      const ref = (r as { externalRef?: { driveId: string; itemId: string } }).externalRef;
      if (ref?.driveId && ref?.itemId) {
        map.set(r.path, { driveId: ref.driveId, itemId: ref.itemId, etag: r.etag ?? '' });
      }
    }
    refMapRef.current = map;
    liveVaultIdRef.current = liveVault?._id ? String(liveVault._id) : null;
  }, [liveNotes, liveVault]);

  // Transport-identity key. The provider is rebuilt when this flips so
  // a freshly-bound live vault stops routing edits through the
  // in-memory transport. Any unflushed pending edits are migrated to
  // the new provider before the old one is disposed.
  const transportKey = liveVault?._id
    ? `sp:${String(liveVault._id)}`
    : 'memory';

  // C.9 / G.1 — save provider. The ref + transport-key pair preserves
  // state through React re-renders, but rebuilds cleanly when the
  // transport flips (memory → SP or different vault).
  const providerRef = useRef<VaultSaveProvider | null>(null);
  const providerKeyRef = useRef<string | null>(null);

  const buildProvider = useCallback(
    (key: string, sourceNotes: ParsedNote[]): VaultSaveProvider => {
      const store = createInMemoryStore(
        sourceNotes.map((n) => ({ path: n.path, body: n.body })),
      );
      const initialEtags: Record<string, string> = {};
      // Live etags win over the synthetic in-memory ones because the
      // transport's If-Match check uses them on every PUT.
      for (const n of sourceNotes) {
        const row = store.read(n.path);
        if (row) initialEtags[n.path] = row.etag;
      }
      for (const [path, ref] of refMapRef.current.entries()) {
        if (ref.etag) initialEtags[path] = ref.etag;
      }
      const useSP = key.startsWith('sp:');
      const transport = useSP
        ? createSharePointTransport({
            resolveRef: (path) => refMapRef.current.get(path) ?? null,
            onEtagBump: (path, newEtag) => {
              const vaultIdStr = liveVaultIdRef.current;
              if (!vaultIdStr) return;
              const body =
                providerRef.current?.getState().pendingByPath[path] ??
                sourceNotes.find((n) => n.path === path)?.body ??
                '';
              void updateNoteAfterPut({
                vaultId: vaultIdStr,
                path,
                body,
                etag: newEtag,
                ...(isDemoForVault ? { demoSessionId: demoSessionIdForVault } : {}),
              }).catch(() => undefined);
            },
          })
        : createInMemoryTransport(store);
      return createSaveProvider({
        debounceMs: 2000,
        initialEtags,
        transport,
        sessionId:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : `sess-${Date.now()}`,
      });
    },
    [updateNoteAfterPut, isDemoForVault, demoSessionIdForVault],
  );

  // First-mount construction.
  if (providerRef.current === null) {
    providerRef.current = buildProvider(transportKey, notes);
    providerKeyRef.current = transportKey;
  }

  // Rebuild on transport-key change. Migrate pending edits so unsaved
  // user typing isn't dropped between memory → SP.
  useEffect(() => {
    if (providerKeyRef.current === transportKey) return;
    const prev = providerRef.current;
    const pending = prev?.getState().pendingByPath ?? {};
    const next = buildProvider(transportKey, notes);
    for (const [path, body] of Object.entries(pending)) {
      next.queueEdit(path, body);
    }
    prev?.dispose();
    providerRef.current = next;
    providerKeyRef.current = transportKey;
  }, [transportKey, buildProvider, notes]);

  const provider = providerRef.current;

  // Tear down on unmount so debounce timers can't outlive the window.
  useEffect(() => {
    return () => {
      providerRef.current?.dispose();
      providerRef.current = null;
    };
  }, []);

  // Take a session-start checkpoint once on mount. Best-effort — the
  // in-memory provider has no writer wired by default; live mode
  // attaches one in C.9+.
  useEffect(() => {
    void provider.takeSessionCheckpoint(notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to provider state via useSyncExternalStore so renders
  // stay precise.
  const saveState: SaveProviderState = useSyncExternalStore(
    useCallback((listener) => provider.subscribe(listener), [provider]),
    () => provider.getState(),
    () => provider.getState(),
  );
  const pendingCount = Object.keys(saveState.pendingByPath).length;
  const savingCount = Object.values(saveState.statusByPath).filter(
    (s) => s === 'saving',
  ).length;
  const conflictCount = Object.values(saveState.statusByPath).filter(
    (s) => s === 'conflict',
  ).length;
  const errorCount = Object.values(saveState.statusByPath).filter(
    (s) => s === 'error',
  ).length;
  const dirty = pendingCount > 0 || savingCount > 0;

  // The search palette is keyed per-portal so warm-cache lookups stay
  // scoped. Outside a portal context (Storybook, raw demo) we fall
  // back to a generic slug so the index still warms.
  const branding = useOptionalTenantBranding();
  const clientSlug = branding?.slug ?? 'global';

  const {
    containerRef,
    widths,
    splitOpen,
    onTreeGripperDown,
    onEditorSplitGripperDown,
    onGraphGripperDown,
    activeGripper,
    openSplit,
    closeSplit,
  } = useResizablePanes(DEFAULT_PANES_CONFIG);

  // Note-lookup maps for wikilink resolution.
  const { notesByPath, notesByName } = useMemo(() => {
    const byPath = new Map<string, ParsedNote>();
    const byName = new Map<string, ParsedNote>();
    for (const n of notes) {
      byPath.set(n.path, n);
      byPath.set(n.path.replace(/\.md$/i, ''), n);
      byName.set(n.name, n);
    }
    return { notesByPath: byPath, notesByName: byName };
  }, [notes]);

  const handleOpen = useCallback(
    (path: string, modifier: 'pane-a' | 'pane-b') => {
      if (modifier === 'pane-b') {
        setPaneBPath(path);
        if (!splitOpen) openSplit();
      } else {
        setPaneAPath(path);
      }
    },
    [openSplit, splitOpen],
  );

  // Close split when pane B is cleared.
  useEffect(() => {
    if (!paneBPath && splitOpen) closeSplit();
  }, [paneBPath, splitOpen, closeSplit]);

  // Cmd / Ctrl + P opens the search palette. Listens on the window so
  // it works while the user is focused inside TipTap (TipTap's own
  // ProseMirror eats most keys; capture phase lets us preempt).
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  // C.14 — reverse-open from the Skill Library. Subscribing here closes
  // the indirection: the Skill Library row dispatches a path; this
  // effect both selects pane A and ensures the window is open + focused.
  useEffect(() => {
    return subscribeVaultSelect((path) => {
      setPaneAPath(path);
      openWindow('/obsidian-vault');
      focusWindow('/obsidian-vault');
    });
  }, [openWindow, focusWindow]);

  const noteA = paneAPath ? notesByPath.get(paneAPath) ?? null : null;
  const noteB = paneBPath ? notesByPath.get(paneBPath) ?? null : null;

  // C.13c — when the active path ends in `.flow`, render FlowEditor in
  // place of VaultEditorPane. Cheap O(1) lookup against the parsed
  // flows list.
  const flowsByPath = useMemo(() => {
    const m = new Map<string, (typeof flows)[number]>();
    for (const f of flows) m.set(f.path, f);
    return m;
  }, [flows]);
  const flowA = paneAPath?.endsWith('.flow')
    ? flowsByPath.get(paneAPath) ?? null
    : null;

  return (
    <div
      data-testid={testIds.obsidianVault.root}
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ece9d8',
        fontFamily: WIN_FONT,
        fontSize: 13,
        lineHeight: 1.4,
      }}
    >
      {/* Toolbar */}
      <div
        data-testid={testIds.obsidianVault.toolbar}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '4px 8px',
          borderBottom: '1px solid #808080',
          background: '#dcdcdc',
          flexShrink: 0,
        }}
      >
        {/* 2026-05-28 — title was previously a hard-coded "Obsidian
            Vault" string. Users couldn't tell whether they were
            looking at the seeded sample or their freshly-imported
            vault. Now we show the live vault's name (e.g.
            "Folder: global22") when an imported vault is bound, and
            mark sample/imported state explicitly. */}
        <strong
          data-testid={testIds.obsidianVault.titleLabel}
          style={{ fontSize: 13 }}
        >
          {liveVault ? liveVault.name : 'Obsidian Vault'}
        </strong>
        <span
          data-testid={testIds.obsidianVault.sourcePill}
          style={{
            fontSize: 10,
            padding: '1px 6px',
            border: '1px solid #808080',
            background: liveVault ? '#cfe8cf' : '#f0e0c0',
            color: '#202020',
            borderRadius: 2,
          }}
          title={
            liveVault
              ? `Source: ${liveVault.source} · imported ${new Date(
                  liveVault.createdAt,
                ).toLocaleString()}`
              : 'Built-in starter vault — not yet imported from your own files'
          }
        >
          {liveVault
            ? liveVault.source === 'sharepoint'
              ? 'imported · SharePoint'
              : 'imported · local'
            : 'sample'}
        </span>
        {sortedVaults.length >= 2 && (
          <select
            data-testid={testIds.obsidianVault.vaultPicker}
            value={effectiveVaultId ?? ''}
            onChange={(e) => {
              // 2026-05-28 — drive selection through the URL so a
              // refresh / share-link keeps the same vault and the
              // wiring stays symmetric with the import wizard's
              // "Open vault" CTA. We pass-through any existing
              // search params (e.g. ?panel=…) so panel auto-open
              // isn't lost when the user swaps vaults mid-session.
              const nextVaultId = e.target.value || undefined;
              void navigate({
                to: '.',
                search: (prev: Record<string, unknown>) => ({
                  ...prev,
                  vault: nextVaultId,
                }),
                replace: true,
              });
            }}
            title="Switch between imported vaults for this portal"
            className="border border-[#808080] bg-white px-1 py-px text-[11px] text-black"
          >
            {sortedVaults.map((v) => (
              <option key={String(v._id)} value={String(v._id)}>
                {v.name}
                {v.createdAt
                  ? ` · ${new Date(v.createdAt).toLocaleDateString()}`
                  : ''}
              </option>
            ))}
          </select>
        )}
        <span style={{ color: '#606060', fontSize: 11 }}>
          {notes.length} notes · {flows.length} whiteboards
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid={testIds.obsidianVault.importVaultButton}
          onClick={() => {
            openWindow('/obsidian-import');
            focusWindow('/obsidian-import');
          }}
          title="Import a vault from SharePoint, ZIP, drag-drop, or local folder"
          style={{
            padding: '2px 8px',
            border: '1px solid #808080',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>📥</span>
          <span>Import vault</span>
        </button>
        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          title="Open search palette (Cmd+P)"
          style={{
            padding: '2px 8px',
            border: '1px solid #808080',
            background: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <span>🔎</span>
          <span>Search</span>
          <span style={{ fontSize: 10, color: '#606060', marginLeft: 4 }}>⌘P</span>
        </button>
        <button
          type="button"
          data-testid={testIds.obsidianVault.editToggle}
          onClick={() => setEditing((x) => !x)}
          style={{
            padding: '2px 8px',
            border: '1px solid #808080',
            background: editing ? '#ffd966' : '#f0f0f0',
            fontWeight: editing ? 'bold' : 'normal',
          }}
        >
          {editing ? 'Stop editing' : 'Edit'}
        </button>
        <button
          type="button"
          data-testid={testIds.obsidianVault.newNoteButton}
          style={{
            padding: '2px 8px',
            border: '1px solid #808080',
            background: '#f0f0f0',
          }}
          disabled
          title="C.9+ will enable new-note creation backed by SharePoint."
        >
          New note
        </button>
        <VaultExportMenu
          notes={notes}
          flows={flows}
          sourceLabel={clientSlug}
          assetRows={assetRows}
          urlForAsset={urlForAsset}
          isOperator={isOperator}
          onExportToSharePointSite={onExportToSharePointSite}
        />
        {paneAPath && (
          <button
            type="button"
            data-testid={testIds.obsidianVault.revertToCheckpoint}
            onClick={() => provider.discard(paneAPath)}
            disabled={
              !saveState.pendingByPath[paneAPath] &&
              saveState.statusByPath[paneAPath] !== 'conflict' &&
              saveState.statusByPath[paneAPath] !== 'error'
            }
            title="Revert the open note to the last saved state"
            style={{
              padding: '2px 8px',
              border: '1px solid #808080',
              background: '#f0f0f0',
            }}
          >
            Revert
          </button>
        )}
        <span
          data-testid={testIds.obsidianVault.saveStatusBadge}
          style={{
            padding: '2px 6px',
            background:
              conflictCount > 0
                ? '#fde6e6'
                : errorCount > 0
                  ? '#fde6e6'
                  : dirty
                    ? '#fff5d6'
                    : '#eef3ff',
            border: `1px solid ${
              conflictCount > 0 || errorCount > 0
                ? '#c43a3a'
                : dirty
                  ? '#c79a14'
                  : '#5b6ee3'
            }`,
            color:
              conflictCount > 0 || errorCount > 0
                ? '#5a0a0a'
                : dirty
                  ? '#5a4308'
                  : '#1a2860',
            fontSize: 11,
          }}
        >
          {conflictCount > 0
            ? `Conflict (${conflictCount})`
            : errorCount > 0
              ? `Error (${errorCount})`
              : savingCount > 0
                ? `Saving (${savingCount})`
                : pendingCount > 0
                  ? `Unsaved (${pendingCount})`
                  : editing
                    ? 'Editing · saved'
                    : 'Read-only'}
        </span>
      </div>

      <SearchPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        clientSlug={clientSlug}
        notes={notes}
        onOpen={handleOpen}
      />

      {/* Three-pane body */}
      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: 'flex',
          minHeight: 0,
        }}
      >
        <div style={{ width: widths.tree, minWidth: widths.tree, height: '100%' }}>
          <VaultTreePane
            notes={notes}
            activePath={paneAPath}
            onOpen={handleOpen}
          />
        </div>
        <div
          className={`vault-gripper${
            activeGripper === 'tree' ? ' vault-gripper-active' : ''
          }`}
          data-testid={testIds.obsidianVault.gripperTreeEditor}
          onMouseDown={onTreeGripperDown}
        />
        <div
          style={{ width: widths.editorA, minWidth: widths.editorA, height: '100%' }}
        >
          {flowA ? (
            <FlowEditor
              path={flowA.path}
              initialRaw={flowA.raw}
              onChange={(nextRaw) => provider.queueEdit(flowA.path, nextRaw)}
              onPopout={() => {
                setActiveFlow({ path: flowA.path, raw: flowA.raw });
                openWindow('/vault-flow');
                focusWindow('/vault-flow');
              }}
            />
          ) : (
            <VaultEditorPane
              note={noteA}
              notesByName={notesByName}
              notesByPath={notesByPath}
              paneId="a"
              onWikilink={handleOpen}
              editing={editing}
              onLocalEdit={(md) => {
                if (!noteA) return;
                provider.queueEdit(noteA.path, md);
              }}
            />
          )}
        </div>
        {splitOpen && (
          <>
            <div
              className={`vault-gripper${
                activeGripper === 'editorSplit' ? ' vault-gripper-active' : ''
              }`}
              data-testid={testIds.obsidianVault.gripperEditorSplit}
              onMouseDown={onEditorSplitGripperDown}
            />
            <div
              style={{
                width: widths.editorB,
                minWidth: widths.editorB,
                height: '100%',
                position: 'relative',
              }}
            >
              <button
                type="button"
                onClick={() => setPaneBPath(null)}
                title="Close split"
                style={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  zIndex: 10,
                  padding: '0 6px',
                  border: '1px solid #808080',
                  background: '#f0f0f0',
                  cursor: 'pointer',
                }}
              >
                ✕
              </button>
              <VaultEditorPane
                note={noteB}
                notesByName={notesByName}
                notesByPath={notesByPath}
                paneId="b"
                onWikilink={handleOpen}
                editing={editing}
                onLocalEdit={(md) => {
                  if (!noteB) return;
                  provider.queueEdit(noteB.path, md);
                }}
              />
            </div>
          </>
        )}
        <div
          className={`vault-gripper${
            activeGripper === 'graph' ? ' vault-gripper-active' : ''
          }`}
          data-testid={testIds.obsidianVault.gripperEditorGraph}
          onMouseDown={onGraphGripperDown}
        />
        <div
          style={{
            width: widths.graph,
            minWidth: widths.graph,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: 1, minHeight: 0 }}>
            <VaultGraphPane
              notes={notes}
              activePath={paneAPath}
              onOpen={handleOpen}
            />
          </div>
          <VaultBacklinksPane
            notes={notes}
            activePath={paneAPath}
            onOpen={handleOpen}
          />
        </div>
      </div>
    </div>
  );
}
